/**
 * @fileoverview The tray's piles and what is done with them: one card per pile (PileCard, with its seed-packet and
 * several-kinds cards), the one sheet where a photograph is moved, given its own group, turned and cropped or removed,
 * the confirm before a whole group goes, and the filing. Shared by the Photos page (PhotoTray, every pile, two
 * columns) and the add pop-ups' "Save" sheet (CheckAndSave, D78: only the batch just added, one column, over
 * the page the gardener is on).
 *
 * Every photo is shown to the model once (useTrayGuesses), the piles drawn being the ones it asks about: the sheet's
 * batch only, never photos left waiting from earlier (those are the Photos page's, a tap away). Agreeing makes the
 * plant with the pile's photos on it, or saves them to one already growing (POST /tray/sort); a photo of seed packets
 * is filed by its card: into a bed (POST /packets/confirm, a bed's own door) or kept in the Seed vault (POST /packets/keep,
 * every other door; B137, afterKept), one of several kinds by its card (POST /tray/kinds). "Ask about it as
 * a plant", in a seed-packet group's ⋯ menu (D78 rev 2 §4: "These aren't seed packets" is gone from the card's face),
 * is the same POST /tray/not-packets.
 *
 * The photo help's allowance (D23) is said once above the piles: a refusal with when some comes back
 * (AiLimitNotice), or the quiet 80% note (AiNearNote). Without the photo AI (Free, a paused plan) nothing is guessed
 * and one line says so with the way to Grow More: the page's words (tray.guessLocked), or in the sheet the owner's
 * shorter "Name each photo yourself, or see what Grow More adds" (review.guessLocked, D78 P6). Switched off by the
 * owner, it is said calmly with nothing to buy (billing.aiRevoked).
 *
 * A move is kept on the server (POST /tray/move), with Undo in its toast; a removed photo is gone at once with a
 * short line and no Undo (B45). A plant's first photo filed here notes the moment to ask about sharing
 * (noteFirstPhoto); the page under it draws ShareMoments.
 *
 * Where: PhotoTray (/plants/photos) and CheckAndSave (Plants, Beds, Seasons, Today).
 * API: POST /tray/guess (useTrayGuesses), /tray/not-packets, /tray/sort, /tray/kinds (KindsPile),
 * /packets/confirm and /packets/keep (PacketPile), /tray/move and /tray/remove (useTrayEdits), PUT /tray/:id/image and
 * /tray/:id/image/reset (Edit image).
 * Settings: TRAY_MOVE_UNDO_TOAST_MS (useTrayGuesses, useTrayEdits); access.ai and
 * access.aiRevoked (useAiAccess).
 * Words (en/es): tray.* (guessLocked, guessLockedAction, sorted*, savedTo*, sortFailed, aPlant, pile.*),
 * review.guessLocked, review.guessLockedAction, billing.aiRevoked, plants.card.editImage.
 * Failures on our side (a 500, no connection; D31): a group that could not be filed says the standard sentence
 * (reported to Sentry, the support address a link) in place of tray.sortFailed (errorMessage); a move or removal
 * that fails is reported and says it in a toast; "Ask about it as a plant" that fails is reported and the card stays
 * as it was.
 *
 * @module TrayPiles
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Crop, Plus, Trash2 } from 'lucide-react';
import { noteFirstPhoto } from '@/features/share/lib/shareMoments';
import { FramedPhoto } from '@/shared/components/ui/FramedPhoto';
import { useToast } from '@/shared/components/ui/use-toast';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useAiAccess } from '@/features/billing/lib/useAiAccess';
import { AiLimitNotice } from '@/features/billing/components/AiLimitNotice';
import { AiNearNote } from '@/features/billing/components/AiNearNote';
import { selectableStages } from '@/shared/lib/stages';
import { usePlantsContext } from '@/shared/hooks/usePlantsContext';
import { useTips } from '@/features/tips/useTips';
import { useStagesContext } from '@/shared/hooks/useStagesContext';
import { useBedNames } from '@/shared/hooks/useBedNames';
import {
  editTrayImage, notPackets, resetTrayImage, sortIntoNewPlant, sortIntoPlant,
  type NewBedRequest, type NewPlantRequest, type TrayPhoto, type TypeUnsureRequest,
} from '@/shared/api/tray';
import { isEditedPhoto } from '@/shared/lib/imageEdit';
import { ImageEditDialog } from '@/features/plant-list/components/dialogs/photo/ImageEditDialog';
import type { KeepResult, PacketResult, PacketScan } from '@/shared/api/packets';
import { usePacketsFiled } from '@/features/packets/usePacketsFiled';
import { usePacketsKept } from '@/features/packets/usePacketsKept';
import { pickerStale } from '@/shared/components/plant-fields/usePlantPicker';
import { debugLog } from '@/config/config';
import { errorMessage } from '@/shared/lib/errorMessage';
import { isServerFailure, reportServerFailure } from '@/shared/lib/serverFailure';
import { BottomPanel } from '@/shared/components/ui/bottom-panel';
import { Button } from '@/shared/components/ui/button';
import { cn } from '@/lib/utils';
import { LOCATIONS_QUERY_KEY, PLANTS_QUERY_KEY } from '@/config/tunables/ui';
import { gardenWritten } from '@/shared/lib/briefFreshness';
import { TRAY_QUERY_KEY, drawPiles, putTrayPhotos, trayPhotoFiled, useTray, type DrawnPile } from '../lib/useTray';
import { useTrayGuesses } from '../lib/useTrayGuesses';
import { useTrayEdits } from '../lib/useTrayEdits';
import { PileCard } from './PileCard';

/** The plants just added from a photo of packets or kinds, and the bed(s) they went to. */
export interface FiledTo {
  /** How many plants (made or joined). */
  count: number;
  /** The bed's name, or the beds' names joined. */
  bed: string;
}

/** What was filed: the plants made or joined, and the beds they went to. */
export interface Filed {
  /** The plants. */
  plantObjIds: number[];
  /** The beds (one made for them among them). */
  bedIds: number[];
}

/** What the piles are given. */
interface TrayPilesProps {
  /** Only the piles holding one of these photos (the add pop-up's batch); null or absent for every pile. */
  only?: ReadonlySet<number> | null;
  /** True while a batch is going in: the piles are still forming, and the guesses wait for it. */
  uploading: boolean;
  /** The Photos page (two columns, its own Free line) or the "Check and save" sheet (one column, the sheet's line). */
  variant?: 'page' | 'sheet';
  /** Drawn in place of the piles when there are none to show: told where the last plants went, if any. */
  empty?: (filedTo: FiledTo | null) => React.ReactNode;
  /** Told of every filing: the plants and their beds. */
  onFiled?: (filed: Filed) => void;
  /** Added from Beds' photo row (D78 P2/P4): every card's bed starts on "A new bed…". */
  newBed?: boolean;
  /** The sheet's way out for a seed-packet card's Cancel (B137 F1): closes it and keeps the photos; absent on the page. */
  onKeepPhotos?: () => void;
}

/**
 * The piles, their cards and sheets.
 *
 * @param props - TrayPilesProps.
 */
export const TrayPiles: React.FC<TrayPilesProps> = ({ only = null, uploading, variant = 'page', empty, onFiled, newBed = false, onKeepPhotos }) => {
  /** The app's words, in the gardener's language. */
  const { reactTranslate: t } = useTranslation();
  /** Says a filing, or a failure, in a toast. */
  const { toast } = useToast();
  /** The app's query client, for the tray and garden reads after a filing. */
  const queryClient = useQueryClient();
  /** The tray, as the server keeps it (useTray). */
  const { data: trayData } = useTray();
  /** Every photo on the tray, removed ones included (none before it arrives). */
  const photos = React.useMemo(() => trayData?.photos ?? [], [trayData]);
  /** The garden's plants (PlantsContext). */
  const { plants } = usePlantsContext();
  /** The gardener's stages (StagesContext). */
  const { stages } = useStagesContext();
  /** The live beds, read without needing LocationsContext (Plants, Seasons and Today have none). */
  const { data: beds } = useBedNames();
  /** Tells the tips what the gardener has done (a plant added from a pile). */
  const { happened } = useTips();
  /** The beds a pile can be filed into: the live ones. */
  const liveLocations = React.useMemo(() => beds ?? [], [beds]);
  /** The stages a pile can be filed at: Planning and All are the app's bookkeeping, not stages a photo can show. */
  const shownStages = React.useMemo(() => selectableStages(stages).filter((s) => !s.is_hidden), [stages]);
  /** The garden's live plants, for each card's Plant list. */
  const gardenPlants = React.useMemo(() => (plants ?? []).filter((p) => p.status === 1), [plants]);
  /** The piles drawn: the server's, removed photos left out, and in the sheet only the batch's. */
  const piles = React.useMemo(() => {
    const all = drawPiles(trayData);
    return only ? all.filter((pile) => pile.photos.some((photo) => only.has(photo.tray_id))) : all;
  }, [trayData, only]);
  /** Where the last plants filed here went, said when the tray empties; null before any. */
  const [filedTo, setFiledTo] = React.useState<FiledTo | null>(null);
  /** The group the gardener asked to remove, while they are asked whether to. */
  const [confirming, setConfirming] = React.useState<number[] | null>(null);
  /** The photo whose sheet is open (move, own group, edit, remove), or null. */
  const [moving, setMoving] = React.useState<TrayPhoto | null>(null);
  /** The photo being turned and cropped, from the sheet's "Edit image". */
  const [editing, setEditing] = React.useState<TrayPhoto | null>(null);
  /** The drawn pile the sheet's photo is in, or null. */
  const movingFrom = moving ? piles.find((pile) => pile.photos.some((photo) => photo.tray_id === moving.tray_id)) ?? null : null;
  /** The piles with work running (a filing, an "ask as a plant"), by key. */
  const [busyKeys, setBusyKeys] = React.useState<Set<number>>(() => new Set());
  /** Whether the photo help may guess (the plan's), and whether the owner switched it off. */
  const { allowed: aiAllowed, revoked: aiRevoked } = useAiAccess();
  /** Each photo's guess, asked once (useTrayGuesses), and what failed or ran out. */
  const { putPhotos, guessFailed, aiLimit, understanding, packetsFailed, setPacketsFailed } = useTrayGuesses({ piles, uploading, aiAllowed });
  /** Moving and removing photos, kept on the server with Undo (useTrayEdits). */
  const { movePhotos, movePhoto, drop } = useTrayEdits({ photos, closeMoving: () => setMoving(null) });
  /** Says seed packets filed and reads the garden again (usePacketsFiled). */
  const packetsFiled = usePacketsFiled();
  /** Says seed packets kept in the Seed vault and reads the garden again (usePacketsKept). */
  const packetsKept = usePacketsKept();

  /**
   * Marks a pile as working, or done working.
   *
   * @param key - The pile's key.
   * @param on - Working.
   */
  const setBusy = (key: number, on: boolean) => setBusyKeys((current) => {
    const next = new Set(current);
    if (on) next.add(key); else next.delete(key);
    return next;
  });

  /**
   * Marks a pile busy while its work runs; the work's own mutation reports a failure.
   *
   * @param key - The pile's key.
   * @param work - What to do.
   */
  const run = async (key: number, work: () => Promise<unknown>) => {
    setBusy(key, true);
    try { await work(); } catch { /* the mutation has already said so */ } finally { setBusy(key, false); }
  };

  /**
   * What a group's card shows of seed packets: the packets read, 'reading' while the request for them is out, or null for a plant
   * question (not packets, or the reader could not be asked).
   *
   * @param pile - The group.
   */
  const packetsOf = (pile: DrawnPile): PacketScan | 'reading' | null => {
    const lead = pile.lead;
    if (lead.guess.packets !== true || packetsFailed.has(lead.tray_id)) return null;
    if (lead.packets) return lead.packets;
    return understanding.has(lead.tray_id) ? 'reading' : null;
  };

  /**
   * A photo of seed packets, or of several kinds, filed: said, the garden read again, and the photo off the tray at
   * once (the server has already taken it off).
   *
   * @param trayId - The photo.
   * @param result - What was filed.
   */
  const afterPackets = (trayId: number, result: PacketResult): void => {
    packetsFiled(result);
    trayPhotoFiled(queryClient, trayId);
    // Where they went stays on the page when the tray empties (L1: it said only "Nothing to sort").
    const where = (result.beds?.length ?? 0) > 1 ? (result.beds ?? []).map((bed) => bed.location_name).join(', ') : (result.beds?.[0]?.location_name ?? result.location_name);
    // Plants that joined one already in the bed are there too: counted with the new ones.
    if (result.added + result.joined > 0) setFiledTo({ count: result.added + result.joined, bed: where });
    onFiled?.({ plantObjIds: result.plant_obj_ids, bedIds: (result.beds ?? []).map((bed) => bed.location_id).concat(result.location_id) });
  };

  /**
   * A photo of seed packets kept in the Seed vault (B137): said, the garden read again, and the photo off the tray at once
   * (the server has already taken it off). No plant was made, so nothing is "filed to" a bed.
   *
   * @param trayId - The photo.
   * @param result - What was kept.
   */
  const afterKept = (trayId: number, result: KeepResult): void => {
    packetsKept(result);
    trayPhotoFiled(queryClient, trayId);
  };

  /**
   * "Ask about it as a plant" (a seed-packet group's ⋯ menu): the photo is asked about as a plant, here at once, and
   * on the server for the next visit.
   *
   * @param pile - The group.
   */
  const sayNotPackets = (pile: DrawnPile): Promise<void> => run(pile.key, async () => {
    try {
      putPhotos([await notPackets(pile.lead.tray_id)]);
    } catch (error) {
      debugLog('PhotoTray', 'Not-packets answer not saved', error);
      if (isServerFailure(error)) reportServerFailure(error, 'Tray not packets');
      setPacketsFailed((current) => new Set([...current, pile.lead.tray_id]));
    }
  });

  /**
   * What a group's card shows of a photo of several kinds: its photo, whose guess holds the kinds, or null for a
   * plant question.
   *
   * @param pile - The group.
   */
  const kindsOf = (pile: DrawnPile): TrayPhoto | null => (
    pile.lead.guess.packets !== true && (pile.lead.guess.kinds?.length ?? 0) >= 2 ? pile.lead : null
  );

  /**
   * A photo of several kinds filed: as packets are, and the plant picker's answers read again, since a plant or
   * variety of the gardener's own may have been made for it.
   *
   * @param trayId - The photo.
   * @param result - What was filed.
   */
  const afterKinds = (trayId: number, result: PacketResult): void => {
    afterPackets(trayId, result);
    void pickerStale(queryClient);
  };

  /**
   * After a pile is saved to a plant: says so, then reads the tray and the garden again.
   *
   * @param plantName - The plant it went to, or null when the server did not say.
   * @param count - How many photos.
   * @param made - Whether the plant was new.
   */
  const afterSort = async (plantName: string | null, count: number, made: boolean) => {
    const name = plantName ?? t('tray.aPlant');
    // Said the moment it is saved, where the gardener pressed the button; the garden catches up behind.
    toast({
      title: made
        ? t(count === 1 ? 'tray.sortedOne' : 'tray.sorted', { plant: name, count })
        : t(count === 1 ? 'tray.savedToOne' : 'tray.savedTo', { plant: name, count }),
    });
    // The tray first, so the sorted card goes at once; the garden catches up behind.
    await queryClient.invalidateQueries({ queryKey: TRAY_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: [PLANTS_QUERY_KEY] });
    void queryClient.invalidateQueries({ queryKey: [LOCATIONS_QUERY_KEY] });
    if (made) {
      // A new plant's week, and whether its card offers About (the brief), as the add form asks for them (PlantsContext refreshGarden).
      // The brief, the outlook and the garden place (Today's doors end with the first plant, B98).
      gardenWritten(queryClient);
    }
  };

  /**
   * A pile made into a new plant, its photos on it. A flower filed as a variety of the gardener's own ("Zinnia") is
   * said by that name, and their varieties are read again so the next card lists it.
   */
  const sortNew = useMutation({
    mutationFn: ({ trayIds, plantId, locationId, stageId, varietyId, newVariety }: {
      trayIds: number[]; plantId: number | NewPlantRequest | TypeUnsureRequest; locationId: number | NewBedRequest; stageId: number | null; varietyId: number | null; newVariety: string | null;
    }) => sortIntoNewPlant(trayIds, plantId, locationId, stageId, varietyId, newVariety),
    onSuccess: (result, { newVariety, plantId, locationId }) => {
      noteFirstPhoto(result.plant_obj_id);
      // A variety or a plant of their own was made: the next card's lists have it.
      if (newVariety || (typeof plantId !== 'number' && 'name' in plantId)) void pickerStale(queryClient);
      // The bed the server used, a new one made from the plant card's "A new bed…" too, so it leads the Beds list.
      onFiled?.({ plantObjIds: [result.plant_obj_id], bedIds: result.location_id != null ? [result.location_id] : typeof locationId === 'number' ? [locationId] : [] });
      return afterSort(newVariety ?? result.plant_name, result.photos, true);
    },
    onError: (error) => toast({ title: errorMessage(error, t('tray.sortFailed')), variant: 'destructive' }),
  });
  /** A pile's photos saved to a plant already in the garden. */
  const sortExisting = useMutation({
    mutationFn: ({ trayIds, plantObjId, stageId }: { trayIds: number[]; plantObjId: number; stageId: number | null }) =>
      sortIntoPlant(trayIds, plantObjId, stageId),
    onSuccess: (result) => {
      // Only a plant that had no photo yet: its first one is the moment to ask about sharing.
      const had = (plants ?? []).find((plant) => plant.plant_obj_id === result.plant_obj_id)?.photos?.length ?? 0;
      if (had === 0) noteFirstPhoto(result.plant_obj_id);
      onFiled?.({ plantObjIds: [result.plant_obj_id], bedIds: [] });
      return afterSort(result.plant_name, result.photos, false);
    },
    onError: (error) => toast({ title: errorMessage(error, t('tray.sortFailed')), variant: 'destructive' }),
  });

  /**
   * A drawn pile's photos still to sort: the ones not removed.
   *
   * @param pile - A drawn pile.
   */
  const liveIds = (pile: DrawnPile): number[] => pile.photos.map((photo) => photo.tray_id);
  /**
   * Whether a pile is still waiting on Grow: a photo in it not yet answered, and not given up on.
   *
   * @param pile - A drawn pile.
   */
  const guessingOf = (pile: DrawnPile): boolean => aiAllowed
    && pile.photos.some((photo) => !photo.guess.done && !guessFailed.has(photo.tray_id));
  /**
   * "Yes, one plant": the later pile's photos go into the earlier one, with Undo, as a move does.
   *
   * @param pile - The later pile.
   */
  const joinEarlier = (pile: DrawnPile): void => {
    if (pile.sameAs) movePhotos(liveIds(pile), pile.sameAs.key, t('tray.pile.joined'));
  };
  /**
   * Asks before removing a group; one photo is removed at once, as its × or the sheet's button does.
   *
   * @param trayIds - The photos.
   */
  const askRemove = (trayIds: number[]): void => {
    if (trayIds.length === 1) drop(trayIds); else if (trayIds.length > 1) setConfirming(trayIds);
  };
  /** How many photos the sheet's pile still has to sort. */
  const movingFromLive = movingFrom ? liveIds(movingFrom).length : 0;

  return (
    <>
      {piles.length === 0 ? empty?.(filedTo) ?? null : (
        <>
          {/* Said once above the piles, not on every card. Switched off by the owner: said calmly, with nothing to buy. */}
          {aiRevoked ? (
            <p role="note" className="mb-3 rounded-sm border bg-card px-3 py-2 text-sm text-muted-foreground">{t('billing.aiRevoked')}</p>
          ) : !aiAllowed && (
            <p className={cn('mb-3 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-sm px-3 py-2 text-sm', variant === 'sheet' ? 'bg-muted/60 text-foreground' : 'border bg-card text-muted-foreground')}>
              <span>{t(variant === 'sheet' ? 'review.guessLocked' : 'tray.guessLocked')}</span>
              <Link to="/subscribe" className={cn('font-medium underline underline-offset-2', variant === 'sheet' ? 'py-2 text-primary' : 'text-foreground')}>
                {t(variant === 'sheet' ? 'review.guessLockedAction' : 'tray.guessLockedAction')}
              </Link>
            </p>
          )}
          {/* The photo help's allowance: a refusal with when some comes back, or the quiet 80% note once. */}
          {aiAllowed && (aiLimit ? <AiLimitNotice refusal={aiLimit} className="mb-3" /> : <AiNearNote className="mb-3" />)}
          {/* Two columns at most on the page (it is max-w-3xl); one in the sheet. */}
          <div className={cn('grid grid-cols-1 gap-3', variant === 'page' && 'md:grid-cols-2')}>
            {piles.map((pile) => (
              <PileCard
                key={pile.key}
                pile={pile}
                gardenPlants={gardenPlants}
                locations={liveLocations}
                stages={shownStages}
                /* Waiting from the moment the pile appears until an answer lands or we give up. */
                guessing={guessingOf(pile)}
                busy={busyKeys.has(pile.key)}
                onAddNew={(plantId, varietyId, locationId, stageId, newVariety) => run(pile.key, async () => {
                  await sortNew.mutateAsync({ trayIds: liveIds(pile), plantId, varietyId, locationId, stageId, newVariety });
                  // A plant of their own, as from the add form: Today's welcome is done.
                  happened('plant_added');
                })}
                onAddToExisting={(plantObjId, stageId) => run(pile.key, () =>
                  sortExisting.mutateAsync({ trayIds: liveIds(pile), plantObjId, stageId }))}
                canMove
                onMovePhoto={setMoving}
                onRemovePhoto={(photo) => drop([photo.tray_id])}
                onRemoveGroup={() => askRemove(liveIds(pile))}
                onKeepPhoto={onKeepPhotos}
                packets={packetsOf(pile)}
                onPacketsFiled={(result) => afterPackets(pile.lead.tray_id, result)}
                onPacketsKept={(result) => afterKept(pile.lead.tray_id, result)}
                onNotPackets={() => void sayNotPackets(pile)}
                sameAs={pile.sameAs ? { plant: pile.sameAs.plant, day: pile.sameAs.day } : null}
                onSameAs={() => joinEarlier(pile)}
                kinds={kindsOf(pile)}
                onKindsFiled={(result) => afterKinds(pile.lead.tray_id, result)}
                newBed={newBed}
              />
            ))}
          </div>
        </>
      )}

      {/* The one place a photograph is handled: to another pile (each shown by its first photo), to a pile of its own,
          turned and cropped, off the tray; and the whole group off the tray. */}
      <BottomPanel open={moving !== null} onOpenChange={(open) => { if (!open) setMoving(null); }} title={t('tray.pile.thisPhoto')}>
        {moving && (
          <div className="flex flex-col gap-2">
            <span className="relative block h-14 w-14 overflow-hidden rounded-sm bg-muted data-[small=true]:bg-transparent">
              <FramedPhoto src={moving.thumbUrl} alt="" />
            </span>
            {piles.some((pile) => pile !== movingFrom) && (
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('tray.pile.moveTo')}</p>
            )}
            {piles.filter((pile) => pile !== movingFrom).map((pile) => {
              const count = liveIds(pile).length;
              return (
                <button
                  key={pile.key}
                  type="button"
                  onClick={() => movePhoto(moving, pile.key)}
                  className="flex min-h-12 items-center gap-3 rounded-sm border px-3 text-left text-sm hover:bg-muted"
                >
                  <span className="relative block h-9 w-9 shrink-0 overflow-hidden rounded-sm bg-muted data-[small=true]:bg-transparent">
                    <FramedPhoto src={pile.lead.thumbUrl} alt="" />
                  </span>
                  <span>
                    {[pile.day ?? t('tray.pile.undated'),
                      count === 1 ? t('tray.pile.photosOne') : t('tray.pile.photos', { count })].join(' · ')}
                  </span>
                </button>
              );
            })}
            {movingFromLive > 1 && (
              <button
                type="button"
                onClick={() => movePhoto(moving, 'own')}
                className="flex min-h-12 items-center gap-3 rounded-sm border px-3 text-left text-sm hover:bg-muted"
              >
                <Plus className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                {t('tray.pile.ownGroup')}
              </button>
            )}
            <button
              type="button"
              onClick={() => { setEditing(moving); setMoving(null); }}
              className="flex min-h-12 items-center gap-3 rounded-sm border px-3 text-left text-sm hover:bg-muted"
            >
              <Crop className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              {t('plants.card.editImage')}
            </button>
            <button
              type="button"
              onClick={() => { const id = moving.tray_id; setMoving(null); drop([id]); }}
              className="flex min-h-12 items-center gap-3 rounded-sm border px-3 text-left text-sm text-muted-foreground hover:bg-muted"
            >
              <Trash2 className="h-4 w-4 shrink-0" aria-hidden="true" />
              {movingFromLive > 1 ? t('tray.pile.notThisPhoto') : t('tray.pile.notAPlant')}
            </button>
            {movingFrom && movingFromLive > 1 && (
              <button
                type="button"
                onClick={() => { const from = movingFrom; setMoving(null); askRemove(liveIds(from)); }}
                className="flex min-h-12 items-center gap-3 rounded-sm border px-3 text-left text-sm text-muted-foreground hover:bg-muted"
              >
                <Trash2 className="h-4 w-4 shrink-0" aria-hidden="true" />
                {t('tray.pile.notPlants', { count: movingFromLive })}
              </button>
            )}
            <Button variant="outline" onClick={() => setMoving(null)}>{t('tray.pile.cancel')}</Button>
          </div>
        )}
      </BottomPanel>

      {/* Turn and crop one photo before it is filed. Saved, it comes back under a new name and links; a seed packet
          photo's boxes move with it. */}
      <ImageEditDialog
        isOpen={editing !== null}
        onClose={() => setEditing(null)}
        src={editing?.signedUrl ?? editing?.cardUrl ?? null}
        alt=""
        edited={isEditedPhoto(editing?.filename)}
        onSave={async (edit) => {
          if (!editing) return;
          putTrayPhotos(queryClient, [await editTrayImage(editing.tray_id, edit)]);
        }}
        onReset={async () => {
          if (!editing) return;
          const original = await resetTrayImage(editing.tray_id);
          putTrayPhotos(queryClient, [original]);
          setEditing(original);
        }}
      />

      {/* A whole group is many photos at once: asked first. */}
      <BottomPanel
        open={confirming !== null}
        onOpenChange={(open) => { if (!open) setConfirming(null); }}
        title={confirming ? t('tray.pile.confirmTitle', { count: confirming.length }) : ''}
      >
        {confirming && (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-muted-foreground">{t('tray.pile.confirmBody')}</p>
            <Button variant="destructive" onClick={() => { drop(confirming); setConfirming(null); }}>
              {t('tray.pile.confirmYes', { count: confirming.length })}
            </Button>
            <Button variant="outline" onClick={() => setConfirming(null)}>{t('tray.pile.keep')}</Button>
          </div>
        )}
      </BottomPanel>
    </>
  );
};
